import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { hasInternalApi, internalApiGet, internalApiPost } from './internalApi'
import { supabase } from './supabase'

/**
 * Row shape of the `manager_assignments` table.
 */
export type Assignment = {
  id: string
  liver_id: string
  manager_id: string
  started_at: string
  ended_at: string | null
  change_reason: string | null
  predecessor_manager_id: string | null
  handoff_notes: string | null
  created_at: string
  created_by: string | null
  /** Joined fields supplied by the view query below. */
  manager?: {
    id: string
    name: string | null
    email: string
    department: string | null
  } | null
  predecessor?: {
    id: string
    name: string | null
    email: string
  } | null
}

type AssignmentsResponse = {
  assignments: Assignment[]
}

type AssignmentResponse = {
  assignment: Assignment
}

/**
 * Fetches all assignments for a given liver, ordered newest first.
 * Each row is joined to the manager (and predecessor manager when present)
 * so the UI can render names without an extra round-trip.
 */
export function useLiverAssignments(liverId: string | undefined) {
  return useQuery<Assignment[]>({
    queryKey: ['assignments', 'by-liver', liverId],
    enabled: !!liverId,
    queryFn: async () => {
      if (!liverId) return []
      if (hasInternalApi()) {
        const data = await internalApiGet<AssignmentsResponse>(`/api/livers/${liverId}/assignments`)
        return data.assignments
      }

      const { data, error } = await supabase
        .from('manager_assignments')
        .select(
          `*,
          manager:managers!manager_assignments_manager_id_fkey(id,name,email,department),
          predecessor:managers!manager_assignments_predecessor_manager_id_fkey(id,name,email)`
        )
        .eq('liver_id', liverId)
        .order('started_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as Assignment[]
    }
  })
}

export function useAllAssignments() {
  return useQuery<Assignment[]>({
    queryKey: ['assignments', 'all'],
    queryFn: async () => {
      if (hasInternalApi()) {
        const data = await internalApiGet<AssignmentsResponse>('/api/assignments?limit=1000')
        return data.assignments
      }

      const { data, error } = await supabase
        .from('manager_assignments')
        .select(
          `*,
          manager:managers!manager_assignments_manager_id_fkey(id,name,email,department),
          predecessor:managers!manager_assignments_predecessor_manager_id_fkey(id,name,email)`
        )
        .order('started_at', { ascending: false })
        .limit(1000)
      if (error) throw error
      return (data ?? []) as Assignment[]
    }
  })
}

/**
 * The currently-active assignment for a liver, if any.
 * Computed from the same dataset as useLiverAssignments to share cache.
 */
export function useCurrentAssignment(liverId: string | undefined): Assignment | null {
  const q = useLiverAssignments(liverId)
  if (!q.data) return null
  return q.data.find((a) => a.ended_at === null) ?? null
}

/**
 * Input for changing the manager assigned to a liver.
 */
export type ChangeAssignmentInput = {
  liverId: string
  newManagerId: string
  changeReason: string | null
  handoffNotes: string | null
  effectiveAt?: string // ISO string; defaults to now
}

/**
 * Atomically (best-effort) closes any active assignment and opens a new one.
 *
 * We can't run a real cross-row transaction from the client, so we sequence:
 *   1. close current active (ended_at = effectiveAt)
 *   2. insert new (started_at = effectiveAt, predecessor = previous manager)
 *
 * The unique index on `manager_assignments(liver_id) WHERE ended_at IS NULL`
 * (added in migration init_core_tables) means step 2 will reject if step 1
 * silently failed — so the data stays consistent even if we don't get a
 * txn semantically.
 */
export function useChangeAssignment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (input: ChangeAssignmentInput) => {
      const effectiveAt = input.effectiveAt ?? new Date().toISOString()
      if (hasInternalApi()) {
        const data = await internalApiPost<AssignmentResponse>(
          `/api/livers/${input.liverId}/assignments`,
          {
            new_manager_id: input.newManagerId,
            change_reason: input.changeReason,
            handoff_notes: input.handoffNotes,
            effective_at: effectiveAt
          }
        )
        return data.assignment
      }

      // Look up the current authenticated user → manager row for `created_by`.
      const { data: auth } = await supabase.auth.getUser()
      let createdBy: string | null = null
      if (auth?.user) {
        const { data: meRow } = await supabase
          .from('managers')
          .select('id')
          .eq('user_id', auth.user.id)
          .maybeSingle()
        createdBy = meRow?.id ?? null
      }

      // 1. Close existing active assignment (if any) and capture its manager_id
      //    so we can put it on the new row as predecessor_manager_id.
      const { data: current } = await supabase
        .from('manager_assignments')
        .select('id, manager_id')
        .eq('liver_id', input.liverId)
        .is('ended_at', null)
        .maybeSingle()

      if (current) {
        const { error: closeErr } = await supabase
          .from('manager_assignments')
          .update({ ended_at: effectiveAt })
          .eq('id', current.id)
        if (closeErr) throw closeErr
      }

      // 2. Insert new active assignment.
      const { data: inserted, error: insertErr } = await supabase
        .from('manager_assignments')
        .insert({
          liver_id: input.liverId,
          manager_id: input.newManagerId,
          started_at: effectiveAt,
          ended_at: null,
          change_reason: input.changeReason,
          handoff_notes: input.handoffNotes,
          predecessor_manager_id: current?.manager_id ?? null,
          created_by: createdBy
        })
        .select()
        .single()
      if (insertErr) throw insertErr
      return inserted
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['assignments', 'all'] })
      qc.invalidateQueries({ queryKey: ['assignments', 'by-liver', vars.liverId] })
    }
  })
}
